import { CalendarDaysIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, Spinner, Typography } from "@material-tailwind/react";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { fetchEvents } from "redux/features/Admin/Events/eventSlice";
import { z } from "zod";

interface Event {
  id: number;
  designation: string;
}

const formSchema = z.object({
  events: z.array(z.string()).refine((arg) => {
    return arg.length > 0;
  }, "Merci d'avoir sélectionné au moins un événement."),
});

const LocationEvent: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const evenstStates = useAppSelector((state) => state.featureEvent);

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      events: data?.events ?? [],
    },
    mode: "onSubmit",
  });

  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchEvents());
  }, []);

  useEffect(() => {
    if (data) {
      setValue("events", data.events);
    }
  }, [data]);

  useEffect(() => {
    if (evenstStates.events) {
      setDataLoading(false);
    }
  }, [evenstStates.events]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormThree") {
      handleSubmit(onSubmit, onError)();
    }
  }, [handleForm]);

  const handleSelect = (eventDesignation: string, isChecked: boolean) => {
    const updatedEvents = isChecked
      ? [...(getValues("events") ?? []), eventDesignation]
      : getValues("events")?.filter((event: string) => event !== eventDesignation);

    setValue("events", updatedEvents as string[]);
    trigger("events");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  const onError = (errors: any) => {
    // En cas d'erreur de validation, ne pas passer les données
    // Cela empêchera le parent de passer à l'étape suivante
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        {/* Section Types d'événements */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <span className="text-xl">🎉</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Types d'événements</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sélectionnez les événements que vous souhaitez accueillir
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Types d'événements acceptés *
            </label>

            {dataLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Spinner color="blue" className="w-12 h-12 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Chargement des types d'événements...</p>
                </div>
              </div>
            ) : (
              <div
                className={`grid gap-3 ${evenstStates.events.length > 10 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}
              >
                {evenstStates.events?.map((event: Event) => (
                  <Checkbox
                    key={event.id}
                    name={event.designation}
                    disabled={isLoading}
                    label={
                      <Typography className="text-gray-900 dark:text-white font-medium">
                        {event.designation}
                      </Typography>
                    }
                    checked={!!getValues("events")?.includes(event.designation)}
                    onChange={(e) => handleSelect(event.designation, e.target.checked)}
                  />
                ))}
              </div>
            )}

            {errors.events?.message && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="text-sm text-red-600 dark:text-red-400">{errors.events.message}</div>
              </div>
            )}
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationEvent;
